<template>
  <v-dialog
    :value="value" @input="$emit('input')"
    width="990"
    content-class="gamesystem-update-form"
  >

    <v-card class="">
      <v-card-title class="text-h5 pb-0">
        <template v-if="item.system_id">Update Game System #{{ item.system_id }}</template>
        <template v-else>Create Game System</template>
      </v-card-title>

      <v-card-text>

        <v-row>

          <v-col cols="4" class="pt-0">
            <v-text-field
              class="mb-2 mt-5"
              label="Name"
              v-model="item.name"
              :loading="loading"
              :disabled="loading"
              hide-details
              readonly
              dense
              outlined
            />

            <v-text-field
              class="mb-2 mt-3"
              label="Alias"
              v-model="item.alias"
              :loading="loading"
              :disabled="loading"
              hide-details
              readonly
              dense
              outlined
            />

            <v-switch
              color="green"
              class="switch-deleted mt-0"
              v-model="item.enabled"
              :label="item.enabled ? 'Enabled' : 'Disabled'"
            />

          </v-col>
          <v-col cols="8" class="pt-0">

            <v-label>Default Settings</v-label>

            <v-btn
              :loading="loadingSettings"
              :disabled="loadingSettings || !template"
              color="success"
              class="float-right mb-1"
              x-small
              @click="applyTemplate(item, 'system')"
            >
              Apply Template
            </v-btn>


            <vue-json-editor
              :loading="loading"
              :disabled="loading"
              v-model="item.settings"
              mode="code"
              :modes="[]"
              :show-btns="false"
              :exapndedOnStart="true"
            />
            <non-ascii-alert :source-object="item.settings" />

            <v-expansion-panels
              v-if="template"
              focusable
              tile
              class="entity-system-settings mb-5"
            >
              <v-expansion-panel>
                <v-expansion-panel-header class="entity-system-settings_header">
                  Template Legend
                </v-expansion-panel-header>
                <v-expansion-panel-content>

                  <v-row>
                    <v-col v-for="key of ['system', 'entity_system']" cols="6" :key="key">

                      <fieldset class="px-2 pb-2 group-border" style="height: 100%">
                        <legend class="px-2">Template {{ capitalize(key.replace('_', ' ')) }}</legend>

                        <v-list-item
                          v-for="item of template[key]"
                          dense
                          class="settings-legend"
                          :key="item.key"
                        >
                          <v-list-item-icon>
                            <v-icon>mdi-circle-small</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content class="py-1">
                            <v-list-item-title>
                              {{ item.key }} ({{ item.type }})
                              <template v-if="item.default">, Default: {{ item.default }}</template>
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="item.comment">
                              {{ item.comment }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </fieldset>
                    </v-col>
                  </v-row>

                </v-expansion-panel-content>
              </v-expansion-panel>

            </v-expansion-panels>
          </v-col>
        </v-row>

        <v-divider class="mb-3" />

        <v-label>
          Entity - System
        </v-label>

        <v-btn
          class="float-right mr-2 mb-3"
          icon
          small
          :disabled="disabled"
          @click="addEntitySystem()"
        >
          <v-icon>add</v-icon>
        </v-btn>

        <v-progress-linear
          v-if="loading"
          indeterminate
          color="green"
        />


        <v-expansion-panels
          v-model="expansioonPanels"
          multiple
          hover
          class="entity-system-settings"
        >
          <template v-for="(entitySystem, i) in entitySystems">
            <v-expansion-panel
              v-if="entitySystem != null"
            >
              <v-expansion-panel-header class="entity-system-settings_header">
                <div style="line-height: 28px">
                  {{ (getProject(entitySystem.entity_id) || {}).text }} #{{ entitySystem.entity_id }}

                  <v-icon
                    v-if="!!entitySystem.edited"
                    x-small
                  >
                    mdi-pencil
                  </v-icon>

                  <span
                    v-if="!entitySystem.enable && !entitySystem.edited"
                    class="error--text ml-2"
                    v-text="'Disabled'"
                  />

                  <v-btn
                    v-if="entitySystem.new"
                    class="float-right mr-3"
                    color="error"
                    dense
                    small
                    icon
                    @click="removeEntitySystem(i)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>

                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>

                <v-select
                  single-line
                  v-model="entitySystem.entity_id"
                  :items="getProject(entitySystem.entity_id) ? [getProject(entitySystem.entity_id), ...projectListAvailable] : projectListAvailable"
                  :rules="[v => !!v || 'Project is required']"
                  label="Project"
                  :return-object="false"
                  required
                  auto-select-first
                  @update="entitySystem.edited = true"
                  :suffix="`#${entitySystem.entity_id || ''}`"
                  v-if="entitySystem.new"
                />

                <v-switch
                  v-model="entitySystem.enable"
                  class="mt-0 float-left"
                  dense
                  hide-details
                  label="Enabled"
                  @change="entitySystem.edited = true"
                />

                <v-btn
                  :loading="loadingSettings"
                  :disabled="loadingSettings || !template"
                  color="success"
                  class="float-right mb-1 mt-1"
                  x-small
                  @click="applyTemplate(entitySystem, 'entity_system')"
                >
                  Apply Template
                </v-btn>

                <vue-json-editor
                  :loading="loading"
                  :disabled="loading"
                  @json-change="entitySystem.edited = true"
                  v-model="entitySystem.settings"
                  mode="code"
                  :modes="[]"
                  :show-btns="false"
                  :exapndedOnStart="true"
                />
                <non-ascii-alert :source-object="entitySystem.settings" />
              </v-expansion-panel-content>
            </v-expansion-panel>

          </template>
        </v-expansion-panels>


      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-btn
          color="warning"
          text
          :loading="loading"
          :disabled="loading"
          @click.native="$emit('input')"
        >
          Close
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          text
          :loading="loading"
          :disabled="loading"
          @click="save(item)"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style>

  .gamesystem-update-form .v-card__text {
    height: calc(100vh - 206px);
    overflow: scroll;
  }

  .gamesystem-update-form .jsoneditor-menu {
    display: none !important;
  }

  .gamesystem-update-form div.jsoneditor-mode-code {
    height: 180px;
  }

  .gamesystem-update-form div.jsoneditor-outer {
    padding-top: 0px;
    margin-top: 0px;
  }

  .gamesystem-update-form {
    min-height: calc(100vh - 105px);
  }

  .entity-system-settings .v-badge__badge {
  }

  .entity-system-settings .entity-system-settings_header {
    min-height: 34px;
    padding: 0px 10px;
  }

  .entity-system-settings .entity-system-settings_header .v-chip {
    margin-top: 6px;
  }

  .gamesystem-update-form_legend fieldset {
    height: 100%;
  }

</style>


<script>
  import vueJsonEditor from 'vue-json-editor'

  import { API } from '@/utils/api'
  import { map, isArray, isObject, find, includes, filter, capitalize } from 'lodash'
  import NonAsciiAlert from '../NonAsciiAlert.vue'

  export default {
    props: {
      value: { default: false },
      refresh: { default: false },
      item: { default: () => ({}) },
    },

    components: {
      vueJsonEditor,
      NonAsciiAlert,
    },

    data: () => ({
      showLegend: false,
      loading: false,
      loadingSettings: false,
      template: false,
      templates: [],

      expansioonPanels: [],
      entitySystems: [],
    }),

    watch: {
      value () {
        if (this.value) {
          this.template = false
          this.expansioonPanels = []
          this.loadEntitySystems()
          this.loadSettings()
        }
      },
    },

    computed: {
      disabled: function () {
        return filter(this.entitySystems, (o) => !!o).length === this.projectList.length
      },
      projectListAvailable: function () {
        return filter(map(this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id })), (o) => !includes(map(this.entitySystems, 'entity_id'), o.value))
      },
      projectList: function () {
        return map(this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id }))
      },
    },

    methods: {
      isArray,
      isObject,
      capitalize,

      addEntitySystem () {
        if (this.disabled) return

        this.entitySystems.push({
          new: true,
          edited: true,
          system_id: this.item.system_id,
          entity_id: this.projectListAvailable[0].value,
          settings: {}
        })
      },

      removeEntitySystem (index) {
        this.entitySystems[index] = null
        this.entitySystems = [ ...this.entitySystems ]
      },

      getProject (entity_id) {
        return (find(this.projectList, { value: entity_id }))
      },

      loadSettings () {
        this.loadingSettings = true
        return API.call('/settings/gamesystem/settings.json', {}).then(resp => {
          this.templates = resp.data

          this.template = find(this.templates, { alias: this.item.alias })

        }).finally(() => {
          this.loadingSettings = false
        })
      },

      applyTemplate (target, type = 'system') {
        if (!this.template || !this.template[type]) return

        target.settings = { ...target.settings }

        let defaultValues = {
          string: '',
          array: [],
          number: 0,
          bool: false,
          default: null
        }

        for (let item of this.template[type]) {
          let value = defaultValues.default
          if (item.hasOwnProperty('default')) {
            value = item.default
          } else if (defaultValues.hasOwnProperty(item.type)) {
            value = defaultValues[item.type]
          }

          if (!target.settings[item.key]) {
            target.settings[item.key] = value
          }
        }
      },

      loadEntitySystems () {
        this.entitySystems = []
        if (!this.item.system_id) return
        this.loading = true
        return API.call('/settings/gamesystem/entity_system_list.json', { system_id: this.item.system_id }).then(resp => {
          this.entitySystems = resp.data
        }).finally(() => {
          this.loading = false
        })
      },
      async save (item, message = 'Save paysystem', saveAndClose = true) {
        let buttons = []
        if (!saveAndClose) {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        } else {
          buttons = [
            { text: 'Save', value: true, class: 'primary darken-1' },
            { text: 'Save and close', value: 'close', class: 'primary darken-1' },
            { text: 'Cancel', value: false, class: 'grey' },
          ]
        }

        let confirmRes = await this.$root.$confirm('Confirm', message, { width: 515, buttons })

        if (!confirmRes) {
          return
        }

        this.loading = true
        return API.call('/settings/gamesystem/update.json', { items: [ { system: item, entity_systems: filter(this.entitySystems, { edited: true }) } ] }).then(resp => {

          this.$notification.show = true
          this.$notification.message = 'Paysystems updated'
          this.$notification.type = 'success'

          return this.refresh()
        }).finally(() => {
          this.loading = false

          if (confirmRes == 'close') {
            this.$emit('input')
          }
        })
      }
    }
  }
</script>
